/**
 * Client Schedule Tab
 * 
 * Shows upcoming visits, assigned caregivers, and schedule summary.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, Clock, User, Users, MapPin, Plus, Search,
  ChevronRight, CheckCircle, AlertCircle, UserPlus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";

const API = process.env.REACT_APP_BACKEND_URL;

// Visit status styles
const VISIT_STATUS_STYLES = {
  scheduled: "bg-blue-100 text-blue-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-800",
  missed: "bg-red-100 text-red-800",
};

export default function ClientScheduleTab({ client, summary, onUpdate }) {
  const [visits, setVisits] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (client?.id) {
      fetchData();
    }
  }, [client?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch upcoming visits
      const today = new Date().toISOString().split("T")[0];
      const visitsRes = await fetch(
        `${API}/api/scheduling/visits?client_id=${client.id}&start_date=${today}&limit=20`,
        { credentials: "include" }
      );
      if (visitsRes.ok) {
        setVisits(await visitsRes.json());
      }
      
      // Fetch assigned caregivers
      const caregiversRes = await fetch(
        `${API}/api/matching/client/${client.id}/caregivers`,
        { credentials: "include" }
      );
      if (caregiversRes.ok) {
        setCaregivers(await caregiversRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch schedule data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading schedule...
      </div>
    );
  }

  const primaryCaregiver = caregivers.find(c => c.is_primary);
  const upcomingVisits = visits.filter(v => 
    ["scheduled", "confirmed"].includes(v.status)
  );

  return (
    <div className="space-y-6" data-testid="schedule-tab">
      {/* Schedule Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">
                  {summary.hours_completed_this_week?.toFixed(1) || "0"}
                </p>
                <p className="text-xs text-muted-foreground">Hours Completed</p>
                <p className="text-xs text-muted-foreground">This Week</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold">
                  {summary.hours_remaining_this_week?.toFixed(1) || summary.hours_authorized_weekly?.toFixed(1) || "0"}
                </p>
                <p className="text-xs text-muted-foreground">Hours Remaining</p>
                <p className="text-xs text-muted-foreground">of {summary.hours_authorized_weekly || 0}/week</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {summary.visits_completed_this_week || 0}
                </p>
                <p className="text-xs text-muted-foreground">Visits Completed</p>
                <p className="text-xs text-muted-foreground">of {summary.visits_this_week || 0} scheduled</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className={summary.open_shifts_count > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className={`text-3xl font-bold ${summary.open_shifts_count > 0 ? "text-amber-600" : ""}`}>
                  {summary.open_shifts_count || 0}
                </p>
                <p className="text-xs text-muted-foreground">Open Shifts</p>
                <p className="text-xs text-muted-foreground">Need Coverage</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Assigned Caregivers */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assigned Caregivers
            </CardTitle>
            <Link to={`/matching?client=${client.id}`}>
              <Button variant="ghost" size="sm">
                <UserPlus className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {caregivers.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No caregivers assigned</p>
                <Link to={`/matching?client=${client.id}`}>
                  <Button variant="outline" size="sm">
                    <Search className="w-4 h-4 mr-2" />
                    Find Matches
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {caregivers.map((cg) => (
                  <div 
                    key={cg.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                        style={{ backgroundColor: cg.is_primary ? "hsl(var(--primary))" : "#6b7280" }}
                      >
                        {(cg.caregiver_name || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{cg.caregiver_name || "Unknown"}</p>
                        {cg.caregiver_phone && (
                          <a 
                            href={`tel:${cg.caregiver_phone}`}
                            className="text-xs text-muted-foreground hover:text-primary"
                          >
                            {cg.caregiver_phone}
                          </a>
                        )}
                      </div>
                    </div>
                    {cg.is_primary && (
                      <Badge className="bg-primary/10 text-primary border-primary/20">Primary</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Visits */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Visits
            </CardTitle>
            <Link to={`/scheduling?client=${client.id}`}>
              <Button variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Schedule
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingVisits.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-sm text-muted-foreground mb-4">No upcoming visits scheduled</p>
                <Link to={`/scheduling?client=${client.id}`}>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule First Visit
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingVisits.slice(0, 5).map((visit) => (
                  <div 
                    key={visit.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[60px]">
                        <p className="text-xs text-muted-foreground">
                          {new Date(visit.visit_date).toLocaleDateString("en-US", { weekday: "short" })}
                        </p>
                        <p className="text-lg font-bold">
                          {new Date(visit.visit_date).getDate()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(visit.visit_date).toLocaleDateString("en-US", { month: "short" })}
                        </p>
                      </div>
                      
                      <Separator orientation="vertical" className="h-12" />
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">
                            {visit.start_time} - {visit.end_time}
                          </span>
                          <Badge 
                            variant="outline" 
                            className={VISIT_STATUS_STYLES[visit.status] || ""}
                          >
                            {visit.status}
                          </Badge>
                        </div>
                        {visit.caregiver_name && (
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <User className="w-3 h-3" />
                            {visit.caregiver_name}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                ))}
                
                {upcomingVisits.length > 5 && (
                  <Link 
                    to={`/scheduling?client=${client.id}`}
                    className="block text-center text-sm text-primary hover:underline pt-2"
                  >
                    View all {upcomingVisits.length} upcoming visits
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Next Visit Card */}
      {summary?.next_visit_date && (
        <Card className="bg-gradient-to-r from-blue-50 to-white border-blue-100">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Next Visit</p>
                  <p className="font-semibold">
                    {new Date(summary.next_visit_date).toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric"
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {summary.next_visit_time} • {summary.next_visit_caregiver || "Unassigned"}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
